/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import urlJoin from 'proper-url-join';

import { PaginatedResult, StoreQueryParams } from '../../../../ts';
import { Assistant, Environment, Multilingual } from '../../../../ts/assistant';
import { endpoints } from '../../util/config';

export interface PaginatedAssistants extends PaginatedResult {
  assistants: Assistant[];
}

export interface PaginatedEnvironments extends PaginatedResult {
  assistants: Environment[];
}

const endpoint = (id?: string) =>
  urlJoin(`${endpoints.store}/v2/assistants`, id);

const auditParams: StoreQueryParams = {
  include_audit: true,
  verbose: true
};

export const listAssistants = async (
  params: StoreQueryParams
): Promise<PaginatedAssistants> => {
  const response = await axios.get<PaginatedAssistants>(endpoint(), {
    params: {
      ...auditParams,
      sort: 'name',
      ...params
    }
  });

  return response.data;
};
export const getAssistant = async (
  assistantId: string,
  params?: StoreQueryParams
): Promise<Assistant> => {
  const response = await axios.get<Assistant>(endpoint(assistantId), {
    params: {
      ...auditParams,
      ...params
    }
  });

  return response.data;
};

export const createAssistant = async (
  assistant: Partial<Assistant>,
  params: StoreQueryParams
): Promise<Assistant> => {
  const response = await axios.post<Assistant>(endpoint(), assistant, {
    params: { ...auditParams, ...params }
  });

  return response.data;
};

export const deleteAssistant = async (id: string): Promise<Assistant> => {
  const response = await axios.delete(endpoint(id));

  return response.data;
};

export const updateAssistant = async (
  id: string,
  data: Partial<Assistant>,
  params: StoreQueryParams
): Promise<Assistant> => {
  const response = await axios.post<Assistant>(endpoint(id), data, {
    params: { ...auditParams, ...params }
  });

  return response.data;
};

/**
 * Create a multilingual import
 * Import multilingual Zip file artifacts that would have been exported by client
 * Note: must include 'Content-Type' as 'application/octet-stream' in the request header otherwise backend will return HTTP 415
 */
export const createMultilingualImport = async (
  assistantId: string,
  data: any,
  params?: StoreQueryParams
): Promise<Multilingual> => {
  const response = await axios
    .post(`${endpoint(assistantId)}/multi_lingual/import`, data, {
      headers: { 'Content-Type': 'application/octet-stream' },
      params: {
        ...auditParams,
        ...params
      }
    })
    .catch(function (error) {
      return {
        data: error.response.data
      };
    });
  return response.data;
};

/**
 * Get the response of a particular multilingual import created by the client
 */
export const getMultilingualImport = async (
  assistantId: string
): Promise<Multilingual> => {
  const response = await axios.get(
    `${endpoint(assistantId)}/multi_lingual/import`,
    {
      params: {
        ...auditParams
      }
    }
  );

  return response.data;
};

/**
 * Create assistant release import
 * Import assistant Zip file artifacts that would have been exported by client
 * Note: must include 'Content-Type' as 'application/octet-stream' in the request header otherwise backend will return HTTP 415
 */
export const createAssistantReleaseImport = async (
  assistantId: string,
  data: any,
  params?: StoreQueryParams
): Promise<Multilingual> => {
  const response = await axios
    .post(`${endpoint(assistantId)}/import`, data, {
      headers: { 'Content-Type': 'application/octet-stream' },
      params: {
        ...auditParams,
        ...params
      }
    })
    .catch(function (error) {
      return {
        data: error.response.data
      };
    });
  return response.data;
};

/**
 * Get the response of a particular assistant release import created by the client
 */
export const getAssistantReleaseImport = async (
  assistantId: string
): Promise<Multilingual> => {
  const response = await axios.get(`${endpoint(assistantId)}/import`, {
    params: {
      ...auditParams
    }
  });

  return response.data;
};
